import {
  PLAFOND_CNAMGS,
  TAUX_CNAMGS,
  TAUX_CNAMGS_FAM_EMP,
  TAUX_CNSS,
  TAUX_CNSS_PF_EMP,
  TAUX_CNSS_RP_EMP,
  TAUX_CNSS_VID_EMP,
  TAUX_TCS,
} from '@shared/constants/Taux';
import {
  parametresPaieAu,
  type AbattementFraisProParams,
  type ContributionsEnAttente,
  type TrancheIRPP,
} from '@shared/constants/ParametresPaieDates';

/**
 * Paramètres fiscaux / sociaux injectables (stockables en `Tenant.settings.paie_fiscale`).
 * Défauts = table de paramètres DATÉS (`ParametresPaieDates.ts`, version applicable à la période)
 * pour SMIG / RMM / seuil TCS / barème IRPP / abattement, et `Taux.ts` pour les taux sociaux.
 */
export interface ParametresFiscauxPaie {
  /** Date d'effet de la version de paramètres utilisée (traçabilité). */
  dateEffet: string;
  /** SMIG : contrôle du salaire de base. N'intervient dans aucun calcul de cotisation. */
  smig: number;
  /** Revenu mensuel minimum : contrôle d'alerte du revenu mensuel total. */
  rmm: number;
  /** TCS : part mensuelle exonérée (CGI art. 347), distincte du SMIG et du RMM. */
  tcsSeuil: number;
  plafondCnss: number;
  /** Plafond mensuel assiette CNAMGS (défaut 2 500 000 ; DEC-03 en attente). */
  plafondCnamgs: number;
  tauxCnssSal: number;
  tauxCnssPfEmp: number;
  tauxCnssRpEmp: number;
  tauxCnssVidEmp: number;
  tauxCnamgsSal: number;
  tauxCnamgsEmp: number;
  tauxCnamgsFamEmp: number;
  tauxTcs: number;
  /** CFP patronale : taux EFFECTIF (0 tant que la contribution est désactivée, à valider expert). */
  tauxTfp: number;
  /** FNH patronale : taux EFFECTIF (0 tant que désactivée, à valider expert). */
  tauxCds: number;
  /** CSS salariale : taux EFFECTIF (0 tant que désactivée, à valider expert). */
  tauxCss: number;
  /** Seuil CSS (480 000, gelé) : sans effet tant que le taux est 0. */
  seuilCss: number;
  /** FGTLS patronale : taux EFFECTIF (0 tant que désactivée, à valider expert). */
  tauxFgtls: number;
  /** État et taux « si activée » de CSS / FGTLS / FNH / CFP (informatif ; le calcul lit les taux effectifs). */
  contributionsEnAttente: ContributionsEnAttente;
  /** Frais professionnels art. 94 (20 %, plafond 10 M/an), activable (défaut : non). */
  abattementFraisPro: AbattementFraisProParams;
  /** Exonération art. 91 11° (IRPP et TCS, plafond annuel et global de 4 000 000) : défaut de la table datée (oui). */
  exonerationArt9111Active: boolean;
  baremeIrpp: readonly TrancheIRPP[];
}

/** @param periode période du bulletin ('AAAA-MM') ou date ; absent = version la plus récente. */
export function defaultParametresFiscauxPaie(periode?: string): ParametresFiscauxPaie {
  const v = parametresPaieAu(periode);
  const ce = v.contributionsEnAttente;
  return {
    dateEffet: v.date_effet,
    smig: v.smig,
    rmm: v.rmm,
    tcsSeuil: v.tcsSeuil,
    plafondCnss: TAUX_CNSS.PLAFOND_MENSUEL,
    plafondCnamgs: PLAFOND_CNAMGS,
    tauxCnssSal: TAUX_CNSS.SALARIAL,
    tauxCnssPfEmp: TAUX_CNSS_PF_EMP,
    tauxCnssRpEmp: TAUX_CNSS_RP_EMP,
    tauxCnssVidEmp: TAUX_CNSS_VID_EMP,
    tauxCnamgsSal: TAUX_CNAMGS.SALARIAL,
    tauxCnamgsEmp: TAUX_CNAMGS.PATRONAL,
    tauxCnamgsFamEmp: TAUX_CNAMGS_FAM_EMP,
    tauxTcs: TAUX_TCS,
    tauxTfp: ce.cfp.actif ? ce.cfp.taux : 0,
    tauxCds: ce.fnh.actif ? ce.fnh.taux : 0,
    tauxCss: ce.css.actif ? ce.css.taux : 0,
    seuilCss: ce.css.seuil,
    tauxFgtls: ce.fgtls.actif ? ce.fgtls.taux : 0,
    contributionsEnAttente: structuredClone(ce),
    abattementFraisPro: { ...v.abattementFraisPro },
    exonerationArt9111Active: v.exonerationArt9111Active,
    baremeIrpp: v.baremeIrpp,
  };
}

/** Fusionne un patch partiel (ex. settings tenant) sur les défauts CGI 2026. */
export function resolveParametresFiscauxPaie(
  override?: Partial<ParametresFiscauxPaie> | null,
  periode?: string,
): ParametresFiscauxPaie {
  const base = defaultParametresFiscauxPaie(periode);
  if (!override) return base;
  return {
    ...base,
    ...override,
    baremeIrpp: override.baremeIrpp ?? base.baremeIrpp,
    abattementFraisPro: { ...base.abattementFraisPro, ...(override.abattementFraisPro ?? {}) },
  };
}

/** Lit `Tenant.settings.paie_fiscale` si présent. */
export function parametresFromTenantSettings(
  settings: Record<string, unknown> | null | undefined,
  periode?: string,
): ParametresFiscauxPaie {
  const raw = settings?.['paie_fiscale'];
  if (!raw || typeof raw !== 'object') return defaultParametresFiscauxPaie(periode);
  return resolveParametresFiscauxPaie(raw as Partial<ParametresFiscauxPaie>, periode);
}
